/**
 * AskTheClub — floating AI Q&A widget (AARP "Ask AARP" pattern).
 * Anon session with localStorage-persistent conversation for the tab.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, X, Loader2, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'aadc_ask_session';

const SUGGESTED_QUESTIONS = [
  'What breeds are good with kids?',
  'How often should I take my dog to the vet?',
  'What is FCI and how does it work?',
  'Should I adopt or buy a puppy?',
];

export default function AskTheClub() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ask-club`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, session_id: sessionId }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessages((m) => [...m, { role: 'assistant', content: data.answer }]);
        if (data.session_id && data.session_id !== sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem(SESSION_KEY, data.session_id);
        }
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: data.detail || 'Sorry, I ran into an issue.', error: true }]);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: 'Network error — please try again.', error: true }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-40 group inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground pl-3 pr-5 py-3 shadow-hover transition-all active:scale-95"
          data-testid="ask-club-open-btn"
          aria-label="Ask the Club"
        >
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent text-foreground">
            <Sparkles className="w-4 h-4" />
          </span>
          <span className="font-heading font-semibold text-sm">Ask the Club</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed inset-x-4 bottom-24 sm:right-6 sm:left-auto sm:bottom-24 z-40 w-[min(420px,calc(100vw-2rem))]" data-testid="ask-club-panel">
          <div className="rounded-2xl border border-border/70 bg-card shadow-hover overflow-hidden flex flex-col max-h-[70vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent text-foreground">
                  <Sparkles className="w-4 h-4" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-sm leading-tight">Ask the Club</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-primary-foreground/70">AI · trusted club sources</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-full hover:bg-primary-foreground/10 transition"
                data-testid="ask-club-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground font-body">
                    Ask about breeds, health, adoption, training, kennel clubs — or anything dog. I keep answers short and always point you to a vet for medical questions.
                  </p>
                  <div className="space-y-1.5">
                    {SUGGESTED_QUESTIONS.map((q) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        className="w-full text-left text-xs rounded-full border border-border/70 bg-card px-3 py-1.5 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                        data-testid={`ask-club-suggest-${q.slice(0, 12).replace(/[^a-z]/gi, '-').toLowerCase()}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`rounded-2xl px-3.5 py-2.5 max-w-[85%] text-sm leading-relaxed font-body ${
                      m.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                        : m.error
                          ? 'bg-destructive/10 text-destructive rounded-bl-sm'
                          : 'bg-muted text-foreground rounded-bl-sm'
                    }`}
                    data-testid={`ask-club-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> thinking…
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 px-3 py-3 border-t border-border/70 bg-card"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything about dogs…"
                disabled={loading}
                className="flex-1 rounded-full border border-border/70 bg-background px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
                data-testid="ask-club-input"
              />
              <Button
                type="submit"
                size="sm"
                disabled={loading || !input.trim()}
                className="h-9 w-9 p-0 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-40"
                data-testid="ask-club-send-btn"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
            <p className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-t border-border/60 flex items-center gap-1 bg-muted/30">
              <MapPin className="w-3 h-3" /> For local answers, use <a href="/discover" className="underline hover:text-primary">/discover</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
